import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { QUERY_KEY_PORTAL_GET_CONTACT, useGetContact } from '@apps-common/apis/portal/contact/get-contact'
import { useUpdateContact } from '@apps-common/apis/portal/contact/update-contact'
import { ButtonRounded } from '@apps-common/components/elements/button'
import { InputSM } from '@apps-common/components/elements/form'
import { ModalBody, ModalContainer, ModalFooter, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { NavbarIcon } from '@apps-common/components/navigation'
import { ModalDismiss } from '@apps-common/config/modal'
import { EMAIL_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '@apps-common/config/regex'
import { queryClient } from '@apps-common/lib'
import { IContactModel } from '@apps-common/types/portal/contact'

export const AccountDetailsUpdateModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: any
}) => {
  const { data: contact, isLoading: isLoadingContact, isError: isErrorContact, isRefetching: isRefetchingContact } = useGetContact({})
  const { mutate: updateContact, isPending: updateContactIsPending } = useUpdateContact(true, () => {
    onDismiss(null, ModalDismiss.Confirm)
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY_PORTAL_GET_CONTACT] })
  })

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty },
    reset
  } = useForm<IContactModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    if (contact) {
      reset(contact)
    }
  }, [contact])

  const onSubmit = (values: IContactModel) => updateContact({ model: values })

  const loading = isLoadingContact || isErrorContact
  const updating = isSubmitting || updateContactIsPending || isRefetchingContact

  return (
    <ModalContainer innerClasses='!h-auto !min-h-0'>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Update' title='Account details' />
          <div className='flex items-center justify-end gap-[15px]'>
            <NavbarIcon icon='close' disabled={loading || updating} callback={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[100px]'>
        <form id='accountDetailsUpdateForm' noValidate onSubmit={handleSubmit(onSubmit)}>
          <InputSM
            type='text'
            placeholder='Enter your name'
            label='Name'
            id='yourName'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('name', { required: true })}
            errors={errors.name}
          />
          <InputSM
            type='text'
            placeholder='Enter your email'
            label='Email'
            id='yourEmail'
            showLabel
            info='Email is connected to your portal account and cannot currently be updated.'
            containerClassName='mb-4'
            loading={loading}
            disabled
            registration={register('email', {
              required: true,
              pattern: {
                value: EMAIL_VALIDATION_REGEX,
                message: 'Enter a valid email'
              }
            })}
            errors={errors.email}
          />
          <InputSM
            type='text'
            placeholder='Enter your contact phone'
            label='Phone'
            id='yourPhone'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('phone', {
              required: true,
              pattern: {
                value: PHONE_VALIDATION_REGEX,
                message: 'Enter a valid NZ phone number'
              }
            })}
            errors={errors.phone}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='w-full flex items-center justify-start gap-2'>
          <ButtonRounded
            form='accountDetailsUpdateForm'
            type='submit'
            loading={updating}
            text='Save'
            className='!h-[30px] min-h-0 !min-w-[120px] mt-0 !w-auto'
          />
          <ButtonRounded
            type='button'
            loading={updating}
            text='Cancel'
            className='!h-[30px] min-h-0 !min-w-[120px] mt-0 !w-auto'
            callback={() => onDismiss(null, 'cancel')}
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
